import React, { Component } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import { FormGroup, FormControl, Dropdown } from "react-bootstrap";
import { TabletOrDesktop, Mobile } from "utils/responsive";
import { EmployeeLink } from "components/Employees/EmployeeEditDrawer";
import { FiMoreHorizontal, FiAlertTriangle } from "react-icons/fi";
import Moment from "react-moment";
import Loading from "components/Loading";
import Layout from "components/Layout";
import Modal from "components/Modal";
import moment from "moment";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";
import { DateRangePicker } from "react-dates";
import ExpenseEditModal from "components/Expenses/ExpenseEditModal";
import { showModal, hideModal } from "actions/modalActions";
import * as expensesActions from "actions/expensesActions";
import EmployeeSelectSearch from "components/EmployeeSelectSearch/EmployeeSelectSearch";
import { expensesService, expStatus } from "services";
import DataTable from "components/DataTable/DataTable";
import "components/Expenses/Expenses.scss";
import ExpenseNavigationTabs from "components/Expenses/ExpenseNavigationTabs";

const APPROVED_SCROLL_ID = "approved-expenses-scroll";

class ApprovedExpenses extends Component {
	constructor(props) {
		super(props);

		this.state = {
			focusedInput: null,
			employee_id: "",
			filterProviderId: "",
			filterExpenseTypeId: "",
			start_from: null,
			start_to: null,
			page: 0,
			page_size: 20,
			approved_expenses: [],
			hasMore: true,
			isLoading: false,
		};

		this.closeModal = this.closeModal.bind(this);
		this.openEditExpenseModal = this.openEditExpenseModal.bind(this);
		this.openAlertModal = this.openAlertModal.bind(this);
		this.getApprovedExpenses = this.getApprovedExpenses.bind(this);
		this.employeeSelectFetchOptions = { enabled: true };
	}

	componentDidMount() {
		Promise.all([
			this.getApprovedExpenses(),
			this.props.getAllExpenseTypes(),
			this.props.getAllProviders(),
		]);
	}

	handleChangeSearch = (event) => {
		const target_id = event.target.id;
		this.setState(
			{
				[target_id]: event.target.value,
				page: 0,
				approved_expenses: [],
				hasMore: true,
			},
			() => this.getApprovedExpenses()
		);
	};

	handleEmployeeFilterChange = (idOrNull) => {
		this.setState(
			{
				employee_id: idOrNull || "",
				page: 0,
				approved_expenses: [],
				hasMore: true,
			},
			() => this.getApprovedExpenses()
		);
	};

	onDatesChange = ({ startDate, endDate }) => {
		if (startDate) startDate = startDate.format("YYYY-MM-DD");
		if (endDate) endDate = endDate.format("YYYY-MM-DD");
		this.setState(
			{
				start_from: startDate,
				start_to: endDate,
				page: 0,
				approved_expenses: [],
				hasMore: true,
			},
			() => this.getApprovedExpenses()
		);
	};

	async getApprovedExpenses() {
		this.setState({ isLoading: this.state.page === 0 });
		const {
			employee_id,
			filterProviderId,
			filterExpenseTypeId,
			start_from,
			start_to,
			page,
			page_size,
			approved_expenses,
		} = this.state;

		try {
			const response = await expensesService.getAll({
				status: expStatus.approved,
				employee_id: employee_id || undefined,
				provider_id: filterProviderId || undefined,
				expense_type_id: filterExpenseTypeId || undefined,
				date_from: start_from,
				date_to: start_to,
				page,
				page_size,
			});

			const new_expenses = response.data || [];
			const total_items =
				response.pagination?.total_items || response.total || 0;
			const all_expenses =
				page === 0 ? new_expenses : approved_expenses.concat(new_expenses);
			const hasMore =
				new_expenses.length > 0 &&
				(total_items > 0
					? all_expenses.length < total_items
					: new_expenses.length === page_size);

			this.setState({
				approved_expenses: all_expenses,
				isLoading: false,
				hasMore,
			});
		} catch (error) {
			console.error("Error fetching approved expenses:", error);
			this.setState({ isLoading: false, hasMore: false });
		}
	}

	fetchMoreData = () => {
		this.setState({ page: this.state.page + 1 }, () =>
			this.getApprovedExpenses()
		);
	};

	reload = () => {
		this.setState({ page: 0, approved_expenses: [], hasMore: true }, () =>
			this.getApprovedExpenses()
		);
	};

	closeModal() {
		this.props.hideModal();
	}

	openEditExpenseModal(expense) {
		const { t } = this.props;
		this.props.showModal(
			{
				open: true,
				title: t("expenses.viewExpense"),
				style: { width: "600px" },
				content: (
					<ExpenseEditModal
						expense={expense}
						getExpenses={this.reload}
						handleClose={this.closeModal}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	}

	openAlertModal(message, title) {
		this.props.showModal(
			{
				open: true,
				title: title || this.props.t("common.error"),
				style: { width: "400px" },
				message,
				closeModal: this.closeModal,
			},
			"alert"
		);
	}

	openDisapproveModal(expense) {
		const { t } = this.props;
		this.props.showModal(
			{
				open: true,
				title: t("expenses.disapproveExpense"),
				style: { width: "400px" },
				message: t("expenses.disapproveConfirm"),
				deleteText: t("expenses.disapprove"),
				loadingText: t("expenses.disapproving"),
				deleteAction: () => this.handleDisapproveExpense(expense),
				closeModal: this.closeModal,
			},
			"delete"
		);
	}

	handleDisapproveExpense = async (expense) => {
		const updated = {
			id: expense.id,
			day: expense.day,
			value: expense.value,
			type_id: expense.type_id ?? expense.type?.id,
			provider_id: expense.provider?.id ?? expense.provider_id,
			comments: expense.comments,
			answer_comments: expense.answer_comments,
			payer_comments: expense.payer_comments,
			status: expStatus.pending,
		};
		const response = await expensesService.update(updated);
		this.closeModal();

		const { t } = this.props;
		if (response.ok) {
			toastr.success(t("common.success"), t("expenses.expenseDisapproved"));
			this.reload();
		} else {
			const msg = response.errors?.[0]?.description || t("common.errorGeneric");
			this.openAlertModal(msg, t("expenses.errorDisapprove"));
		}
	};

	getActions = (expense) => (
		<Dropdown>
			<Dropdown.Toggle variant="action">
				<FiMoreHorizontal />
			</Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item
					as="button"
					onClick={() => this.openEditExpenseModal(expense)}
				>
					{this.props.t("expenses.viewDetail")}
				</Dropdown.Item>
				<Dropdown.Item
					as="button"
					onClick={() => this.openDisapproveModal(expense)}
				>
					{this.props.t("expenses.disapproveExpense")}
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);

	getColumns = () => {
		const { expense_types, t } = this.props;
		return [
			{
				key: "requester",
				header: t("expenses.columns.requester"),
				sortable: false,
				render: (expense) => {
					const employee = expense.requester;
					return (
						<div className="employee-info-wrapper">
							<div className="employee-avatar-wrapper">
								{employee?.name ? employee.name.charAt(0).toUpperCase() : ""}
								{employee?.surname
									? employee.surname.charAt(0).toUpperCase()
									: ""}
							</div>
							<div className="employee-details">
								<div className="flex flex-row">
									<EmployeeLink
										employee={employee}
										className="employee-name-link"
									>
										{employee ? `${employee.name} ${employee.surname}` : ""}
									</EmployeeLink>
								</div>
								<div className="employee-meta">
									<Moment format="DD/MM/YYYY HH:mm">{expense.requested}</Moment>
								</div>
							</div>
						</div>
					);
				},
			},
			{
				key: "day",
				header: t("expenses.columns.date"),
				render: (expense) => <Moment format="DD/MM/YYYY">{expense.day}</Moment>,
			},
			{
				key: "value",
				header: t("expenses.columns.amount"),
				render: (expense) => {
					const maxValue = expense.type?.max_value;
					const exceeded = maxValue && parseFloat(expense.value) > parseFloat(maxValue);
					return (
						<span className={exceeded ? "expense-value-exceeded" : undefined}>
							{expense.value} €
							{exceeded && (
								<FiAlertTriangle
									className="icon-limit-exceeded"
									title={t("expenses.columns.limitExceeded")}
								/>
							)}
						</span>
					);
				},
			},
			{
				key: "type_name",
				header: t("expenses.columns.category"),
				render: (expense) => {
					const typeObj = expense.type ?? expense_types.find((et) => et.id === expense.type_id);
					return (
						<span
							className="expense-type-tag"
							style={{
								backgroundColor: typeObj?.background_color || undefined,
								border: typeObj?.border_color ? `2px solid ${typeObj.border_color}` : undefined,
							}}
						>
							{typeObj?.name ?? expense.type_name}
						</span>
					);
				},
			},
			{
				key: "answered_at",
				header: t("expenses.columns.approvalDate"),
				render: (expense) =>
					expense.answered_at ? (
						<Moment format="DD/MM/YYYY HH:mm">{expense.answered_at}</Moment>
					) : (
						"—"
					),
			},
		];
	};

	getContent = () => {
		if (this.state.isLoading && this.state.page === 0) {
			return <Loading />;
		}

		const { hasMore, approved_expenses } = this.state;

		if (!this.state.isLoading && approved_expenses.length === 0) {
			return <p>{this.props.t("expenses.approved.noResults")}</p>;
		}

		return (
			<DataTable
				scrollContainerId={APPROVED_SCROLL_ID}
				tableClassName="table-expenses table-desktop table-hover"
				columns={this.getColumns()}
				rows={approved_expenses}
				rowKey={(row) => row.id}
				emptyMessage={this.props.t("expenses.approved.noResults")}
				actionsColumn={{
					render: (expense) => this.getActions(expense),
					className: "td-actions",
				}}
				infiniteScroll={{
					dataLength: approved_expenses.length,
					next: this.fetchMoreData,
					hasMore,
					loader: (
						<div style={{ textAlign: "center", padding: "10px" }}>
							{this.props.t("common.loading")}
						</div>
					),
					endMessage: approved_expenses.length > 0 && (
						<p
							className="no-more-results"
							style={{ textAlign: "center", padding: "10px" }}
						>
							{this.props.t("expenses.approved.noMore")}
						</p>
					),
				}}
			/>
		);
	};

	render() {
		const { providers, expense_types } = this.props;
		const {
			employee_id,
			filterProviderId,
			filterExpenseTypeId,
			start_from,
			start_to,
			focusedInput,
		} = this.state;

		return (
			<>
				<Layout
					className="page-expenses page-expenses-approved"
					context="expenses"
				>
					<div className="heading">
						<div className="heading-left">
							<h1 className="title">{this.props.t("expenses.approved.title")}</h1>
						</div>
					</div>

					<ExpenseNavigationTabs />

					<div className="subheading">
						<div className="subheading-filters">
							<EmployeeSelectSearch
								className="form-select-user"
								controlId="employee_id"
								multiple={false}
								value={employee_id || null}
								onChange={this.handleEmployeeFilterChange}
								fetchOptions={this.employeeSelectFetchOptions}
								placeholder={this.props.t("common.searchEmployee")}
								allLabel={this.props.t("expenses.allEmployees")}
							/>

							<FormGroup
								controlId="filterProviderId"
								className="form-group form-search"
							>
								<FormControl
									as="select"
									value={filterProviderId}
									onChange={this.handleChangeSearch}
								>
									<option value="">{this.props.t("expenses.allProviders")}</option>
									{providers?.map((item) => (
										<option key={item.id} value={item.id}>
											{item.name}
										</option>
									))}
								</FormControl>
							</FormGroup>

							<FormGroup
								controlId="filterExpenseTypeId"
								className="form-group form-search"
							>
								<FormControl
									as="select"
									value={filterExpenseTypeId}
									onChange={this.handleChangeSearch}
								>
									<option value="">{this.props.t("expenses.allCategories")}</option>
									{expense_types?.map((item) => (
										<option key={item.id} value={item.id}>
											{item.name}
										</option>
									))}
								</FormControl>
							</FormGroup>

							<DateRangePicker
								startDateId="approved_startDate"
								endDateId="approved_endDate"
								startDate={start_from ? moment(start_from) : null}
								endDate={start_to ? moment(start_to) : null}
								startDatePlaceholderText={this.props.t("common.from")}
								endDatePlaceholderText={this.props.t("common.to")}
								onDatesChange={this.onDatesChange}
								focusedInput={focusedInput}
								onFocusChange={(focusedInput) =>
									this.setState({ focusedInput })
								}
								hideKeyboardShortcutsPanel={true}
								isOutsideRange={() => false}
								minimumNights={0}
							/>
						</div>
					</div>

					{this.getContent()}
				</Layout>

				<Modal hideModal={this.props.hideModal} />
			</>
		);
	}
}

const mapStateToProps = (reducers) => ({
	...reducers.expensesReducer,
	user: reducers.authReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) =>
		dispatch(showModal({ modalProps, modalType })),
	getAllExpenseTypes: () => dispatch(expensesActions.getAllExpenseTypes()),
	getAllProviders: () => dispatch(expensesActions.getAllProviders()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ApprovedExpenses));
